import { ajudaDaTela } from '@levitas/contracts';
import { Button, Modal } from '@levitas/ui';
import { CircleHelp, PlayCircle } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * O botão de ajuda que acompanha a tela (M18).
 *
 * Ele abre a explicação **daquela** tarefa, e não uma lista onde a pessoa teria
 * de procurar. Ajuda que exige procurar não é ajuda: quem está com um problema
 * não quer navegar por um manual.
 *
 * Some quando a tela não tem artigo — botão que abre o nada é pior que botão
 * nenhum.
 */
export function BotaoDeAjuda() {
  const { pathname } = useLocation();
  const [aberto, setAberto] = useState(false);

  const artigo = ajudaDaTela(pathname);
  if (!artigo) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setAberto(true)}
        data-testid="botao-de-ajuda"
        aria-label={`Ajuda: ${artigo.titulo}`}
        className="flex size-11 items-center justify-center rounded-[var(--radius-md)] text-[var(--cor-texto-suave)] hover:bg-[var(--cor-superficie-2)] hover:text-[var(--cor-texto)]"
      >
        <CircleHelp aria-hidden className="size-5" />
      </button>

      <Modal aberto={aberto} aoFechar={() => setAberto(false)} titulo={artigo.titulo}>
        <p className="text-sm text-[var(--cor-texto-suave)]">{artigo.resumo}</p>

        <ol className="mt-4 flex flex-col gap-3" data-testid="passos-da-ajuda">
          {artigo.passos.map((passo, indice) => (
            <li key={passo} className="flex gap-3 text-sm">
              <span
                aria-hidden
                className="flex size-6 shrink-0 items-center justify-center rounded-full bg-[var(--brand-suave)] text-xs font-semibold text-[var(--brand-contraste)]"
              >
                {indice + 1}
              </span>
              <span>{passo}</span>
            </li>
          ))}
        </ol>

        {artigo.dicas && artigo.dicas.length > 0 ? (
          <div className="mt-4 rounded-xl border border-[var(--cor-borda)] bg-[var(--cor-superficie-suave)] px-4 py-3">
            <p className="text-xs font-medium">Vale saber</p>
            <ul className="mt-1 list-disc space-y-1 pl-4 text-sm text-[var(--cor-texto-suave)]">
              {artigo.dicas.map((dica) => (
                <li key={dica}>{dica}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <div className="mt-5 flex flex-wrap items-center gap-2">
          {artigo.video ? (
            <Link
              to={`/ajuda/${artigo.slug}`}
              onClick={() => setAberto(false)}
              className="inline-flex h-11 items-center gap-2 rounded-[var(--radius-md)] bg-[var(--brand)] px-4 text-sm font-medium text-[var(--brand-texto)]"
              data-testid="ver-o-video"
            >
              <PlayCircle aria-hidden className="size-4" />
              Ver em vídeo
            </Link>
          ) : null}

          <Link
            to="/ajuda"
            onClick={() => setAberto(false)}
            className="inline-flex h-11 items-center rounded-[var(--radius-md)] border border-[var(--cor-borda-forte)] px-4 text-sm font-medium"
          >
            Central de ajuda
          </Link>

          <Button variante="fantasma" onClick={() => setAberto(false)}>
            Fechar
          </Button>
        </div>
      </Modal>
    </>
  );
}
